import { createCrudApi } from './crud';
import { apiClient } from './client';
import type { ApiSuccess } from '../types';
import type { BulkResult } from './bulkOnboard.api';

/** Allowed departments — must match the server-side enum in employee.validation.ts. */
export const DEPARTMENTS = ['Protection Device Installation', 'Repair and Maintenance', 'EPC'] as const;
export type Department = (typeof DEPARTMENTS)[number];

export interface Employee {
  id: number;
  employeeCode: string;
  name: string;
  department: string | null;
  designation: string | null;
  reportingManagerId: number | null;
  /** Resolved display name of the reporting manager (read-only, from the API). */
  reportingManagerName: string | null;
  regionId: number | null;
  email: string | null;
  mobile: string | null;
  createdAt: string;
}

export interface CreateEmployeeInput {
  /** Manually-entered, unique Employee ID (required on create; immutable on edit). */
  employeeCode?: string;
  name: string;
  department?: Department | null;
  designation?: string;
  /** Selected by manager NAME in the UI; sent as the relational id. */
  reportingManagerId?: number | null;
  // region_id is preserved server-side for future region mapping; the form no longer sends it.
  regionId?: number | null;
  email?: string;
  mobile?: string;
}

const base = createCrudApi<Employee, CreateEmployeeInput>('employees');

export const employeesApi = {
  ...base,
  /** Bulk-create employees from parsed CSV rows. Shaped to the shared BulkResult. */
  async bulkImport(rows: Record<string, unknown>[]): Promise<BulkResult> {
    const { data } = await apiClient.post<ApiSuccess<{ total: number; created: number; failed: number; results: BulkResult['results'] }>>('/employees/bulk', { rows });
    return { entity: 'employee', loginsCreated: 0, ...data.data };
  },
};
